import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setMapOnFlyInfo } from "@/features/connections/crossReferenceSlice";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";

// Note: filterType/filterStatus/filters/managerSearchString now lives in sessionInfo.workflowManagerSession (workflowManagerSessionSlice)
// Removed legacy filter reducers and selectors, as well as heapIOTrackFilter function

const workflowManagerSlice = createSlice({
  name: "workflowManager",
  initialState: {
    connectionId: -1,
    manager: {
      records: [],
      lastAnalyticsRunUtc: null,
    },
    status: "idle",
    recordsLoaded: false,
    error: null,
    isWorkflowStepsOpen: false,
    workflowSteps: {},
    selectedRecord: null,
    workflowStepsAreLoading: false,
    lifecyclePopupOpen: false,
    lifecycleDetails: [],
    filters: {
      status: [],
      type: [],
      applyFilters: false,
    },
    workflowStepsInfo: {
      workflowRecordId: "",
      sourceArtifactDictionaryDataId: "",
      connectionPairId: -1,
    },
  },
  reducers: {
    setIsWorkflowStepsOpen: (state, action) => {
      const { isOpen } = action.payload;
      state.isWorkflowStepsOpen = isOpen;
    },
    setLifecyclePopupOpen: (state, action) => {
      const { lifecyclePopupOpen } = action.payload;
      state.lifecyclePopupOpen = lifecyclePopupOpen;
    },
    setLifecycleDetails: (state, action) => {
      const { lifecycleDetails } = action.payload;
      state.lifecycleDetails = lifecycleDetails;
    },
    setManagerRecords: (state, action) => {
      const { newRecords } = action.payload;
      state.manager.records = newRecords;
    },
    setSelectedRecord: (state, action) => {
      const { row } = action.payload;
      state.selectedRecord = row;
      state.isWorkflowStepsOpen = true;
    },
    clearSelectedRecord: (state, action) => {
      state.selectedRecord = null;
    },
    clearWorkflowSteps: (state, action) => {
      state.workflowSteps = {};
    },
    updateRecordProperty: (state, action) => {
      const { sourceReferenceIds, sourceConnectionIds, property, value } =
        action.payload;
      state.manager.records = state.manager.records.map((record) => {
        if (
          record &&
          sourceReferenceIds.includes(record.sourceArtifactDictionaryDataId) &&
          sourceConnectionIds.includes(record.sourceConnectionId)
        ) {
          return { ...record, [property]: value };
        }
        return record;
      });
    },
    setApplyFilter: (state, action) => {
      const { applyFilter } = action.payload;
      state.filters.applyFilters = applyFilter;
    },
    setWorkflowStepsInfo: (state, action) => {
      const {
        workflowRecordId,
        sourceArtifactDictionaryDataId,
        connectionPairId,
      } = action.payload;
      state.workflowStepsInfo.workflowRecordId = workflowRecordId;
      state.workflowStepsInfo.sourceArtifactDictionaryDataId =
        sourceArtifactDictionaryDataId;
      state.workflowStepsInfo.connectionPairId = connectionPairId;
    },
    updateWorkflowStatus: (state, action) => {
      const { workflowRecordId, status } = action.payload;
      const recordIndex = state.manager.records.findIndex(
        (record) => record.WorkflowRecordId === workflowRecordId,
      );
      if (recordIndex !== -1) {
        state.manager.records[recordIndex].Status = status;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecordsData.pending, (state, action) => {
        state.recordsLoaded = false;
      })
      .addCase(fetchRecordsData.rejected, (state, action) => {
        state.recordsLoaded = true;
        state.error = action.error.message;
      })
      .addCase(fetchRecordsData.fulfilled, (state, action) => {
        state.recordsLoaded = true;
        state.manager.records = action.payload.records;
        state.manager.lastAnalyticsRunUtc = action.payload.lastAnalyticsRunUtc;
      })
      .addCase(fetchWorkflowSteps.pending, (state, action) => {
        state.status = "loading";
        state.workflowStepsAreLoading = true;
      })
      .addCase(fetchWorkflowSteps.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
        state.workflowStepsAreLoading = false;
      })
      .addCase(fetchWorkflowSteps.fulfilled, (state, action) => {
        state.status = "loaded";
        state.workflowSteps = action.payload;
        state.workflowStepsAreLoading = false;
      });
  },
});

export const fetchMissingCrossReferenceData = createAsyncThunk(
  "workflowManager/fetchMissingCrossReferenceData",
  async ({ currentOperationId }, { dispatch, getState }) => {
    const state = getState();
    const {
      sourceArtifactDictionaryDataId,
      workflowRecordId,
      connectionPairId,
    } = state.tempInfo.workflowManager.workflowStepsInfo;
    axios
      .get("/operationsmanager/unmapped-cross-references", {
        params: {
          sourceArtifactDictionaryDataId,
          operationId: currentOperationId,
          connectionPairId,
        },
      })
      .then((response) => {
        const responseData = response.data;
        if (responseData?.hasOwnProperty("CrossReferenceDisplay")) {
          dispatch(
            setMapOnFlyInfo({
              crossReferenceInfo: responseData.CrossReferenceDisplay,
              crossReferenceWorkflowRecordId: workflowRecordId,
            }),
          );
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message:
              "Encountered problem in fetching unmapped cross-references. Error: " +
              error.data,
          }),
        );
      });
  },
);

export const fetchRecordsData = createAsyncThunk(
  "workflowManager/fetchRecordsData",
  async (params, extra) => {
    const { operationId, startDate, endDate, connectionId, clearCache } =
      params;
    const { getState } = extra; // Destructure the extra inbuilt args to access getstate
    const state = getState();
    let filtersObj = "";

    if (state.sessionInfo.workflowManagerSession.filters.applyFilters) {
      filtersObj = JSON.stringify(
        state.sessionInfo.workflowManagerSession.filters,
      );
    }
    const response = await axios.get("/operationsmanager/manager-records/", {
      params: {
        operationId: operationId,
        startDate: startDate,
        endDate: endDate,
        filters: filtersObj,
        searchFilter:
          state.sessionInfo.workflowManagerSession.managerSearchString,
        connectionId: connectionId,
        clearCache: clearCache,
        searchAll: state.sessionInfo.workflowManagerSession.searchAll,
      },
    });
    let data = [];
    if (response.data.Records.length > 0) {
      data = response.data.Records.map((x, i) => ({ id: i, ...x }));
    }
    return {
      records: data,
      lastAnalyticsRunUtc: response.data?.LastAnalyticsRunUtc ?? null,
    };
  },
);

export const fetchWorkflowSteps = createAsyncThunk(
  "workflowManager/fetchWorkflowSteps",
  async ({ operationId, row }) => {
    const {
      sourceId,
      targetId,
      fromReference,
      sourceConnectionId,
      toReference,
      targetConnectionId,
    } = row;
    const response = await axios.get(
      "/operationsmanager/workflow-record-steps-details/",
      {
        params: {
          operationId: operationId,
          sourceArtifactDictionaryDataId: sourceId,
          targetArtifactDictionaryDataId: targetId,
          sourceArtifactReference: fromReference ?? "",
          sourceConnectionId: sourceConnectionId ?? -1,
          targetArtifactReference: toReference ?? "",
          targetConnectionId: targetConnectionId ?? -1,
        },
      },
    );

    return response.data;
  },
);

//SHEP-82: These are some helper methods that can be easily used to read the redux state into any component that import this method.
export const selectAllRecords = (state) =>
  state.tempInfo.workflowManager.manager.records;
export const selectLastAnalyticsRunUtc = (state) =>
  state.tempInfo.workflowManager.manager.lastAnalyticsRunUtc;
export const selectIsWorkflowStepsOpen = (state) =>
  state.tempInfo.workflowManager.isWorkflowStepsOpen;
export const selectWorkflowSteps = (state) =>
  state.tempInfo.workflowManager.workflowSteps;
export const selectLifecyclePopupOpen = (state) =>
  state.tempInfo.workflowManager.lifecyclePopupOpen;
export const selectLifecycleDetails = (state) =>
  state.tempInfo.workflowManager.lifecycleDetails;
export const selectRecordsLoadedStatus = (state) =>
  state.tempInfo.workflowManager.recordsLoaded;
export const selectWorkflowStepsInfo = (state) =>
  state.tempInfo.workflowManager.workflowStepsInfo;
export const selectSelectedRecord = (state) =>
  state.tempInfo.workflowManager.selectedRecord;
export const selectWorkflowStepsAreLoading = (state) =>
  state.tempInfo.workflowManager.workflowStepsAreLoading;

export const {
  setIsWorkflowStepsOpen,
  setFilterSourceConnections,
  setFilterTargetConnections,
  setDateType,
  setFilterDate,
  setLifecyclePopupOpen,
  setLifecycleDetails,
  setManagerRecords,
  setSelectedRecord,
  clearSelectedRecord,
  clearWorkflowSteps,
  updateRecordProperty,
  setApplyFilter,
  setWorkflowStepsInfo,
  updateWorkflowStatus,
} = workflowManagerSlice.actions;

export default workflowManagerSlice.reducer;
